import React from 'react'
import LegalPageView from './LegalPageView'

export default function TermsOfUseView(): React.JSX.Element {
  return (
    <LegalPageView title="Terms of Use" lastUpdated="July 25, 2026">
      <p>
        These Terms of Use (&ldquo;Terms&rdquo;) govern your access to and use of 9nights&rsquo;
        website, apps, and related services (collectively, the &ldquo;Service&rdquo;). By creating
        an account or otherwise using the Service, you agree to be bound by these Terms. If you do
        not agree, please do not use the Service.
      </p>

      <h2>1. Who Can Use 9nights</h2>
      <p>
        You must be at least 13 years old (or the minimum age required in your country) to use the
        Service. By creating an account, you confirm that you meet this requirement and that the
        information you provide is accurate. You are responsible for keeping your account
        credentials confidential and for all activity that occurs under your account.
      </p>

      <h2>2. The Service</h2>
      <p>
        9nights lets you write, organize, and publish stories, including with the help of
        AI-assisted co-writing tools. Features may change over time — we may add, modify, or remove
        features, and we may suspend or discontinue the Service (or any part of it) at any time,
        though we&rsquo;ll try to give you reasonable notice for changes that materially affect your
        use of the Service.
      </p>

      <h2>3. Your Content</h2>
      <p>
        You retain ownership of the stories, wiki entries, and other content you create on 9nights
        (&ldquo;Your Content&rdquo;). By submitting Your Content to the Service, you grant 9nights a
        worldwide, non-exclusive, royalty-free license to host, store, reproduce, and display Your
        Content solely as necessary to operate and provide the Service to you — for example, to save
        your drafts, render your published stories to readers, and back up your data. This license
        ends when you delete Your Content or your account, except for copies retained for a
        reasonable period for backup, legal, or security purposes.
      </p>
      <p>
        You are solely responsible for Your Content and for having the rights necessary to post it.
        When you publish a story, you are choosing to make it visible to other users or the public,
        as described in our Privacy Policy.
      </p>

      <h2>4. Acceptable Use</h2>
      <p>You agree not to use the Service to:</p>
      <ul>
        <li>
          Post content that is unlawful, defamatory, harassing, or infringes another person&rsquo;s
          rights;
        </li>
        <li>
          Impersonate another person or misrepresent your affiliation with any person or entity;
        </li>
        <li>
          Upload malware, or attempt to gain unauthorized access to the Service or other
          users&rsquo; accounts;
        </li>
        <li>
          Scrape, crawl, or use automated means to access the Service in a way that could disrupt it
          or overburden our infrastructure;
        </li>
        <li>Use the Service to generate or distribute spam or unsolicited communications; or</li>
        <li>Violate any applicable law or regulation.</li>
      </ul>
      <p>
        We may remove content or suspend accounts that we reasonably believe violate these Terms.
      </p>

      <h2>5. Intellectual Property</h2>
      <p>
        Other than Your Content, the Service — including its design, text, graphics, logos, and
        software — is owned by 9nights or our licensors and is protected by intellectual property
        laws. These Terms do not grant you any right to use our trademarks or branding without our
        prior written permission.
      </p>

      <h2>6. Copyright Complaints</h2>
      <p>
        If you believe content on the Service infringes your copyright, please send a notice to{' '}
        <a href="mailto:legal@9nights.app">legal@9nights.app</a> identifying the copyrighted work,
        the material you believe is infringing, and your contact information. We will review and
        respond to valid notices in accordance with applicable law.
      </p>

      <h2>7. Third-Party Services</h2>
      <p>
        The Service may let you sign in through, or link out to, third-party services (such as
        Google). We are not responsible for the content, policies, or practices of third-party
        services, and your use of them is governed by their own terms.
      </p>

      <h2>8. Termination</h2>
      <p>
        You may stop using the Service and delete your account at any time. We may suspend or
        terminate your access to the Service if you violate these Terms, or if we reasonably believe
        doing so is necessary to protect the Service, other users, or third parties. Sections of
        these Terms that by their nature should survive termination (such as ownership, disclaimers,
        and limitations of liability) will continue to apply.
      </p>

      <h2>9. Disclaimers</h2>
      <p>
        The Service, including any AI-generated suggestions, is provided &ldquo;as is&rdquo; and
        &ldquo;as available,&rdquo; without warranties of any kind, whether express or implied,
        including warranties of merchantability, fitness for a particular purpose, and
        non-infringement. AI-assisted suggestions may be inaccurate, incomplete, or unsuitable for
        your purposes, and you are responsible for reviewing and editing any content before relying
        on or publishing it.
      </p>

      <h2>10. Limitation of Liability</h2>
      <p>
        To the fullest extent permitted by law, 9nights and its officers, employees, and affiliates
        will not be liable for any indirect, incidental, special, consequential, or punitive
        damages, or any loss of data, revenue, or goodwill, arising out of or related to your use of
        the Service, even if we have been advised of the possibility of such damages. Our total
        liability for any claim arising out of these Terms or the Service will not exceed the
        greater of $100 or the amount you paid us, if any, in the twelve months before the claim
        arose.
      </p>

      <h2>11. Indemnification</h2>
      <p>
        You agree to indemnify and hold 9nights harmless from any claims, losses, or expenses
        (including reasonable attorneys&rsquo; fees) arising out of Your Content, your violation of
        these Terms, or your misuse of the Service.
      </p>

      <h2>12. Governing Law</h2>
      <p>
        These Terms are governed by the laws of the jurisdiction in which 9nights is established,
        without regard to conflict-of-law principles. Any disputes arising out of these Terms or the
        Service will be resolved in the courts located in that jurisdiction, unless applicable law
        requires otherwise.
      </p>

      <h2>13. Changes to These Terms</h2>
      <p>
        We may update these Terms from time to time. If we make material changes, we will notify you
        by posting the updated Terms on this page and updating the &ldquo;Last updated&rdquo; date
        above. Continuing to use the Service after changes take effect means you accept the updated
        Terms.
      </p>

      <h2>14. Contact Us</h2>
      <p>
        If you have questions about these Terms, contact us at{' '}
        <a href="mailto:legal@9nights.app">legal@9nights.app</a>.
      </p>
    </LegalPageView>
  )
}
